import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import {
  getOrderSequenceSettings,
  resetOrderCounters,
  goLiveOrderReset,
  clearAllBusinessData,
  downloadFullBackupZip,
  getShopSettings,
  updateShopSettings,
} from '../utils/api';

const GO_LIVE_CONFIRM = 'DELETE ALL ORDERS';
const CLEAR_ALL_CONFIRM = 'CLEAR ALL DATA';

const SettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [resetRetail, setResetRetail] = useState(true);
  const [resetWholesale, setResetWholesale] = useState(true);
  const [resetPassword, setResetPassword] = useState('');
  const [resetConfirmed, setResetConfirmed] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [goLivePassword, setGoLivePassword] = useState('');
  const [goLiveConfirmText, setGoLiveConfirmText] = useState('');
  const [goLiveConfirmed, setGoLiveConfirmed] = useState(false);
  const [goLiveRunning, setGoLiveRunning] = useState(false);
  const [clearAllPassword, setClearAllPassword] = useState('');
  const [clearAllConfirmText, setClearAllConfirmText] = useState('');
  const [clearAllConfirmed, setClearAllConfirmed] = useState(false);
  const [clearAllRunning, setClearAllRunning] = useState(false);
  const [clearAllPhase, setClearAllPhase] = useState(null);
  const [shopPaymentEnabled, setShopPaymentEnabled] = useState(false);
  const [catalogUpdatingBannerEnabled, setCatalogUpdatingBannerEnabled] = useState(false);
  const [shopSettingsLoading, setShopSettingsLoading] = useState(true);
  const [shopPaymentSaving, setShopPaymentSaving] = useState(false);
  const [catalogBannerSaving, setCatalogBannerSaving] = useState(false);

  const loadShopSettings = useCallback(async () => {
    try {
      setShopSettingsLoading(true);
      const data = await getShopSettings();
      setShopPaymentEnabled(Boolean(data.shopPaymentEnabled));
      setCatalogUpdatingBannerEnabled(Boolean(data.catalogUpdatingBannerEnabled));
    } catch (error) {
      console.error('Error loading shop settings:', error);
      toast.error(error.message || 'Failed to load shop settings');
    } finally {
      setShopSettingsLoading(false);
    }
  }, []);

  const handleShopPaymentToggle = async () => {
    const next = !shopPaymentEnabled;
    setShopPaymentSaving(true);
    try {
      const data = await updateShopSettings({ shopPaymentEnabled: next });
      setShopPaymentEnabled(Boolean(data.shopPaymentEnabled));
      toast.success(
        next
          ? 'Shop online payment enabled — Razorpay checkout is live'
          : 'Shop online payment disabled — customers submit inquiries'
      );
    } catch (error) {
      toast.error(error.message || 'Failed to update shop settings');
    } finally {
      setShopPaymentSaving(false);
    }
  };

  const handleCatalogBannerToggle = async () => {
    const next = !catalogUpdatingBannerEnabled;
    setCatalogBannerSaving(true);
    try {
      const data = await updateShopSettings({ catalogUpdatingBannerEnabled: next });
      setCatalogUpdatingBannerEnabled(Boolean(data.catalogUpdatingBannerEnabled));
      toast.success(
        next
          ? 'Catalog updating banner enabled on shop'
          : 'Catalog updating banner disabled on shop'
      );
    } catch (error) {
      toast.error(error.message || 'Failed to update shop settings');
    } finally {
      setCatalogBannerSaving(false);
    }
  };

  const loadStatus = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getOrderSequenceSettings();
      setStatus(data);
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error(error.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
    loadShopSettings();
  }, [loadStatus, loadShopSettings]);

  const handleResetCounters = async () => {
    const types = [];
    if (resetRetail) types.push('retail');
    if (resetWholesale) types.push('wholesale');

    if (types.length === 0) {
      toast.error('Select at least one counter to reset');
      return;
    }
    if (!resetPassword.trim() || !resetConfirmed) return;

    setResetting(true);
    try {
      const result = await resetOrderCounters({ password: resetPassword.trim(), types });
      setStatus(result.status);
      setResetPassword('');
      setResetConfirmed(false);
      toast.success(result.message || 'Counters reset');
    } catch (error) {
      toast.error(error.message || 'Failed to reset counters');
    } finally {
      setResetting(false);
    }
  };

  const handleGoLiveReset = async () => {
    if (!goLivePassword.trim() || !goLiveConfirmed) return;
    if (goLiveConfirmText !== GO_LIVE_CONFIRM) {
      toast.error(`Type exactly: ${GO_LIVE_CONFIRM}`);
      return;
    }

    setGoLiveRunning(true);
    try {
      const result = await goLiveOrderReset({
        password: goLivePassword.trim(),
        confirmText: goLiveConfirmText,
      });
      setStatus(result.status);
      setGoLivePassword('');
      setGoLiveConfirmText('');
      setGoLiveConfirmed(false);
      toast.success(result.message || 'Go-live reset complete');
    } catch (error) {
      toast.error(error.message || 'Go-live reset failed');
    } finally {
      setGoLiveRunning(false);
    }
  };

  const handleClearAllData = async () => {
    if (!clearAllPassword.trim() || !clearAllConfirmed) return;
    if (clearAllConfirmText !== CLEAR_ALL_CONFIRM) {
      toast.error(`Type exactly: ${CLEAR_ALL_CONFIRM}`);
      return;
    }

    setClearAllRunning(true);
    let phase = 'backup';
    setClearAllPhase(phase);
    try {
      toast.loading('Downloading full backup ZIP…', { id: 'clear-all-data' });
      await downloadFullBackupZip();

      phase = 'clear';
      setClearAllPhase(phase);
      toast.loading('Clearing photos and data…', { id: 'clear-all-data' });
      const result = await clearAllBusinessData({
        password: clearAllPassword.trim(),
        confirmText: clearAllConfirmText,
      });
      setStatus(result.status);
      setClearAllPassword('');
      setClearAllConfirmText('');
      setClearAllConfirmed(false);
      const warnCount = result.warnings?.length || 0;
      toast.success(
        warnCount > 0
          ? `Backup downloaded. ${result.message || 'Cleared'} (${warnCount} photo warning${warnCount === 1 ? '' : 's'})`
          : `Backup downloaded. ${result.message || 'All photos and data cleared'}`,
        { id: 'clear-all-data' }
      );
    } catch (error) {
      toast.error(
        error.message ||
          (phase === 'clear'
            ? 'Failed to clear all photos and data'
            : 'Backup failed — data was not cleared'),
        { id: 'clear-all-data' }
      );
    } finally {
      setClearAllRunning(false);
      setClearAllPhase(null);
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto">
      <Toaster position="top-right" />

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Settings</h1>
        <p className="text-sm text-gray-600 mt-1">
          Order numbering, go-live, and full data wipe. Users and shop payment settings are never deleted from this page.
        </p>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-2">Shop payment gateway</h2>
        <p className="text-sm text-gray-600 mb-4">
          When off, customers submit inquiries (optional WhatsApp) and you approve after UPI advance.
          When on, the public shop uses Razorpay as today.
        </p>
        <label className="flex items-start gap-3 cursor-pointer max-w-xl">
          <input
            type="checkbox"
            checked={shopPaymentEnabled}
            onChange={handleShopPaymentToggle}
            disabled={shopSettingsLoading || shopPaymentSaving}
            className="mt-1 h-4 w-4"
          />
          <span className="text-sm text-gray-700">
            <span className="font-medium text-gray-900 block">
              {shopSettingsLoading
                ? 'Loading…'
                : shopPaymentEnabled
                  ? 'Online payment enabled (Razorpay)'
                  : 'Online payment disabled (inquiry + UPI workflow)'}
            </span>
            {shopPaymentSaving ? 'Saving…' : 'Toggle to switch shop checkout mode.'}
          </span>
        </label>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-2">Shop catalog message</h2>
        <p className="text-sm text-gray-600 mb-4">
          When on, the public shop shows: “More idols are being added — Browse what’s available now —
          new designs are being added. Please check back soon for the full collection.” Turn off when
          the collection is ready.
        </p>
        <label className="flex items-start gap-3 cursor-pointer max-w-xl">
          <input
            type="checkbox"
            checked={catalogUpdatingBannerEnabled}
            onChange={handleCatalogBannerToggle}
            disabled={shopSettingsLoading || catalogBannerSaving}
            className="mt-1 h-4 w-4"
          />
          <span className="text-sm text-gray-700">
            <span className="font-medium text-gray-900 block">
              {shopSettingsLoading
                ? 'Loading…'
                : catalogUpdatingBannerEnabled
                  ? 'Catalog updating banner enabled'
                  : 'Catalog updating banner disabled'}
            </span>
            {catalogBannerSaving
              ? 'Saving…'
              : 'Show or hide the “More idols are being added” message on the shop.'}
          </span>
        </label>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="text-lg font-semibold text-gray-800">Order sequence status</h2>
          <button
            type="button"
            onClick={loadStatus}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>

        {loading && !status ? (
          <p className="text-gray-500 text-sm">Loading…</p>
        ) : status ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-4">
              <p className="text-xs font-medium uppercase tracking-wide text-emerald-800">Retail</p>
              <p className="text-2xl font-bold text-emerald-900 mt-1">{status.retail.nextOrderNo}</p>
              <p className="text-sm text-emerald-800/80 mt-1">
                Prefix {status.retail.prefix} · counter {status.retail.counter}
              </p>
            </div>
            <div className="rounded-lg border border-indigo-200 bg-indigo-50/50 p-4">
              <p className="text-xs font-medium uppercase tracking-wide text-indigo-800">Wholesale</p>
              <p className="text-2xl font-bold text-indigo-900 mt-1">{status.wholesale.nextOrderNo}</p>
              <p className="text-sm text-indigo-800/80 mt-1">
                Prefix {status.wholesale.prefix} · counter {status.wholesale.counter}
              </p>
            </div>
            <div className="sm:col-span-2 text-sm text-gray-600">
              Orders in database: {status.orders.total} total
              ({status.orders.retail} retail, {status.orders.wholesale} wholesale)
            </div>
          </div>
        ) : null}
      </div>

      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-2">Reset counters only</h2>
        <p className="text-sm text-gray-600 mb-4">
          Restarts numbering from 001 for selected types. Existing orders are kept.
        </p>
        <div className="flex flex-col gap-3 max-w-lg">
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={resetRetail}
              onChange={(e) => setResetRetail(e.target.checked)}
            />
            Reset retail counter (RET-001 next)
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={resetWholesale}
              onChange={(e) => setResetWholesale(e.target.checked)}
            />
            Reset wholesale counter (WHO-001 next)
          </label>
          <input
            type="password"
            value={resetPassword}
            onChange={(e) => setResetPassword(e.target.value)}
            placeholder="Admin password (CLEAR_DATA_PASSWORD)"
            className="toolbar-input max-w-full"
            autoComplete="off"
          />
          <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={resetConfirmed}
              onChange={(e) => setResetConfirmed(e.target.checked)}
              className="mt-0.5"
            />
            <span>I understand the next order will restart from 001 for selected types.</span>
          </label>
          <button
            type="button"
            onClick={handleResetCounters}
            disabled={
              resetting ||
              goLiveRunning ||
              clearAllRunning ||
              !resetPassword.trim() ||
              !resetConfirmed ||
              (!resetRetail && !resetWholesale)
            }
            className="px-5 py-3 bg-amber-700 text-white rounded-lg hover:bg-amber-800 disabled:bg-gray-400 disabled:cursor-not-allowed font-semibold text-sm w-fit"
          >
            {resetting ? 'Resetting…' : 'Reset selected counters'}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6 border border-red-100">
        <h2 className="text-lg font-semibold text-red-800 mb-2">Go-live: delete all orders</h2>
        <p className="text-sm text-gray-600 mb-2">
          Deletes every order and resets both counters so the next retail order is RET-001 and wholesale is WHO-001.
          Inventory is not deleted.
        </p>
        <p className="text-sm text-gray-600 mb-4">
          Recommended: download a full backup from{' '}
          <Link to="/admin/finance" className="text-emerald-700 font-medium underline">
            Finance
          </Link>{' '}
          before running this.
        </p>
        <div className="flex flex-col gap-3 max-w-lg">
          <input
            type="password"
            value={goLivePassword}
            onChange={(e) => setGoLivePassword(e.target.value)}
            placeholder="Admin password (CLEAR_DATA_PASSWORD)"
            className="toolbar-input max-w-full"
            autoComplete="off"
          />
          <input
            type="text"
            value={goLiveConfirmText}
            onChange={(e) => setGoLiveConfirmText(e.target.value)}
            placeholder={`Type ${GO_LIVE_CONFIRM} to confirm`}
            className="toolbar-input max-w-full font-mono text-sm"
            autoComplete="off"
          />
          <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={goLiveConfirmed}
              onChange={(e) => setGoLiveConfirmed(e.target.checked)}
              className="mt-0.5"
            />
            <span>I understand this permanently deletes all orders and cannot be undone from this app.</span>
          </label>
          <button
            type="button"
            onClick={handleGoLiveReset}
            disabled={
              goLiveRunning ||
              resetting ||
              clearAllRunning ||
              !goLivePassword.trim() ||
              !goLiveConfirmed ||
              goLiveConfirmText !== GO_LIVE_CONFIRM
            }
            className="px-5 py-3 bg-red-700 text-white rounded-lg hover:bg-red-800 disabled:bg-gray-400 disabled:cursor-not-allowed font-semibold text-sm w-fit"
          >
            {goLiveRunning ? 'Deleting…' : 'Delete all orders and reset numbering'}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6 mt-6 border border-red-200">
        <h2 className="text-lg font-semibold text-red-900 mb-2">Clear all photos and data</h2>
        <p className="text-sm text-gray-600 mb-2">
          Automatically downloads a full backup ZIP first, then permanently deletes inventory
          (including Cloudinary photos), all orders, order/inquiry counters, and shop inquiries.
          Users, login history, subscriptions, and shop payment settings are kept.
        </p>
        <p className="text-sm text-gray-600 mb-4">
          If the backup download fails, nothing is cleared. Restore later from{' '}
          <Link to="/admin/finance" className="text-emerald-700 font-medium underline">
            Finance
          </Link>{' '}
          using the downloaded ZIP.
        </p>
        <div className="flex flex-col gap-3 max-w-lg">
          <input
            type="password"
            value={clearAllPassword}
            onChange={(e) => setClearAllPassword(e.target.value)}
            placeholder="Admin password (CLEAR_DATA_PASSWORD)"
            className="toolbar-input max-w-full"
            autoComplete="off"
          />
          <input
            type="text"
            value={clearAllConfirmText}
            onChange={(e) => setClearAllConfirmText(e.target.value)}
            placeholder={`Type ${CLEAR_ALL_CONFIRM} to confirm`}
            className="toolbar-input max-w-full font-mono text-sm"
            autoComplete="off"
          />
          <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={clearAllConfirmed}
              onChange={(e) => setClearAllConfirmed(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              I understand this permanently deletes inventory, photos, orders, and inquiries and cannot be undone from this app.
            </span>
          </label>
          <button
            type="button"
            onClick={handleClearAllData}
            disabled={
              clearAllRunning ||
              goLiveRunning ||
              resetting ||
              !clearAllPassword.trim() ||
              !clearAllConfirmed ||
              clearAllConfirmText !== CLEAR_ALL_CONFIRM
            }
            className="px-5 py-3 bg-red-900 text-white rounded-lg hover:bg-red-950 disabled:bg-gray-400 disabled:cursor-not-allowed font-semibold text-sm w-fit"
          >
            {clearAllPhase === 'backup'
              ? 'Backing up…'
              : clearAllPhase === 'clear'
                ? 'Clearing…'
                : 'Clear all photos and data'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
